import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Assets/logo.png';

export default function CaptureCam() {
  const videoRef = useRef(null);
  const [captured, setCaptured] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if mediaDevices is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorMessage('Camera access requires HTTPS. Please use HTTPS or access from localhost.');
      return;
    }

    navigator.mediaDevices.getUserMedia({ video: true })
      .then((stream) => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch((error) => {
        console.error('Camera access error:', error);
        if (error.name === 'NotAllowedError') {
          setErrorMessage('Camera access denied. Please allow camera permissions.');
        } else if (error.name === 'NotFoundError') {
          setErrorMessage('No camera found on this device.');
        } else if (error.name === 'NotReadableError') {
          setErrorMessage('Camera is already in use by another application.');
        } else if (error.name === 'OverconstrainedError') {
          setErrorMessage('Camera constraints could not be satisfied.');
        } else {
          setErrorMessage('Camera access requires HTTPS. Please use HTTPS or localhost.');
        }
      });
  }, []);

  const handleCapture = async (e) => {
    e.preventDefault();
    const video = videoRef.current;

    const fullWidth = video.videoWidth;
    const fullHeight = video.videoHeight;
    
    // For maximum SDXL compatibility, use square dimensions
    // 512x512 is the most reliable size for SDXL models
    const targetWidth = 512;  // SDXL standard square
    const targetHeight = 512; // SDXL standard square
    
    // Calculate the crop area from the video to get a centered face
    // Most webcams are landscape, so we need to crop to portrait
    let cropWidth, cropHeight;
    let sourceX, sourceY;
    
    // We want to capture the center portion of the video as a square
    const videoAspectRatio = fullWidth / fullHeight;
    const targetAspectRatio = 1; // Square aspect ratio (512x512)
    
    if (videoAspectRatio > targetAspectRatio) {
      // Video is wider than our target - crop the sides
      cropHeight = fullHeight;
      cropWidth = fullHeight * targetAspectRatio;
      sourceX = (fullWidth - cropWidth) / 2;
      sourceY = 0;
    } else {
      // Video is taller than our target - crop top and bottom
      cropWidth = fullWidth;
      cropHeight = fullWidth / targetAspectRatio;
      sourceX = 0;
      sourceY = (fullHeight - cropHeight) / 2;
    }
    
    // Ensure we don't exceed video dimensions
    cropWidth = Math.min(cropWidth, fullWidth);
    cropHeight = Math.min(cropHeight, fullHeight);
    
    console.log(`📏 Video: ${fullWidth}x${fullHeight}, Crop area: ${Math.round(cropWidth)}x${Math.round(cropHeight)}`);
    console.log(`📐 Output: ${targetWidth}x${targetHeight} (square for SDXL compatibility)`);

    const canvas = document.createElement('canvas');
    // Set canvas to portrait dimensions for SDXL
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    // Espelha horizontalmente (flip horizontal for selfie effect)
    ctx.translate(targetWidth, 0);
    ctx.scale(-1, 1);
    // Draw the cropped portion of video scaled to fit the portrait canvas
    ctx.drawImage(
      video, 
      sourceX, sourceY, cropWidth, cropHeight,  // Source rectangle from video
      0, 0, targetWidth, targetHeight           // Destination rectangle on canvas
    );

    canvas.toBlob(async (blob) => {
      if (!blob) {
        setErrorMessage('Erro ao capturar imagem. Tente novamente.');
        setCaptured(false);
        setLoading(false);
        return;
      }
      const previewURL = URL.createObjectURL(blob);
      setPhoto(previewURL);
      setCaptured(true);
      setLoading(true);

      const cellphone = localStorage.getItem("cellphone");
      const th = localStorage.getItem("th");
      
      if (!th) {
        setErrorMessage('Erro: Empresa não identificada. Acesse através do link fornecido pela Clínica.');
        setCaptured(false);
        setLoading(false);
        return;
      }

      try {
        // 1. Enviar para /create_landmark
        const formData = new FormData();
        formData.append("file", blob, "face.png");
        formData.append("cellphone", cellphone);
        formData.append("th", th);

        const stream = videoRef.current?.srcObject;
        if (stream) {
          const tracks = stream.getTracks();
          tracks.forEach(track => track.stop());
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_AI_URL}/create_landmark`, {
          method: "POST",
          body: formData,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
        });

        if (!response.ok) throw new Error("Erro no envio da imagem");

        const result = await response.json();
        const uuid_value = result.uuid;

        localStorage.setItem("current_task_id", uuid_value);
        formData.append("uuid_value", uuid_value);
        
        // 2. Primeiro /suggest_procedures
        const suggestResponse = await fetch(`${process.env.REACT_APP_BACKEND_AI_URL}/suggest_procedures`, {
          method: "POST",
          body: formData,
        });

        if (!suggestResponse.ok) throw new Error("Erro na sugestão de procedimentos");
        
        // 3. Depois /replicate_face
        const replicateResponse = await fetch(`${process.env.REACT_APP_BACKEND_AI_URL}/replicate_face`, {
          method: "POST",
          body: formData,
        });

        if (!replicateResponse.ok) throw new Error("Erro na replicação da face");
        
        // 4. Quando tudo termina, navega
        // Add a small delay to ensure all async operations are complete
        setTimeout(() => {
          navigate('/processing-status');
        }, 500);

      } catch (err) {
        setErrorMessage('Erro ao processar imagem. Tente novamente.');
        setCaptured(false);
      } finally {
        setLoading(false);
      }
    }, "image/png");
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {loading ? (
        <div className="min-h-screen bg-gradient-to-br from-[#5C2D91] via-[#7B4397] to-[#00B1B1] flex flex-col items-center justify-center px-6">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
            <img src={logo} alt="VisageAI logo" className="block max-w-[120px] w-full h-auto mx-auto mb-6" />
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00B1B1]"></div>
              <p className="text-[#5C2D91] font-semibold">Processando sua imagem...</p>
            </div>
            <p className="text-gray-600 text-sm">Nossa IA está analisando sua foto para criar recomendações personalizadas</p>
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="bg-gradient-to-r from-[#5C2D91] to-[#00B1B1] px-6 py-4 text-white">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/processing-status')}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors mr-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-semibold">Análise Facial VisageAI</h1>
                <p className="text-white/80 text-sm">Posicione seu rosto no centro da tela</p>
              </div>
            </div>
          </div>

          {/* Camera Interface */}
          <div className="flex-1 flex flex-col justify-center px-4 py-6">
            {errorMessage ? (
              <div className="max-w-sm mx-auto w-full">
                <div className="bg-red-50 rounded-3xl p-6 shadow-xl border border-red-200">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-red-800 font-semibold text-lg text-center mb-2">Erro ao acessar câmera</h3>
                  <p className="text-red-600 text-sm text-center mb-4">{errorMessage}</p>
                  
                  {errorMessage.includes('HTTPS') && (
                    <div className="bg-white rounded-2xl p-4 mt-4">
                      <p className="text-gray-700 font-medium text-sm mb-2">Soluções:</p>
                      <ul className="text-gray-600 text-xs space-y-1">
                        <li>• Use o app via HTTPS (recomendado)</li>
                        <li>• Acesse de um computador usando localhost</li>
                        <li>• Configure certificados SSL para desenvolvimento</li>
                      </ul>
                      <button 
                        onClick={() => navigate('/processing-status')} 
                        className="w-full mt-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl py-2 px-4 font-medium text-sm transition-colors"
                      >
                        Voltar aos Resultados
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : !captured ? (
              <div className="relative">
                {/* Video Container */}
                <div className="relative w-full max-w-sm mx-auto aspect-[3/4] rounded-3xl overflow-hidden bg-black shadow-2xl">
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    className="w-full h-full object-cover scale-x-[-1]" 
                  />
                  
                  {/* Overlay with face guide */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/20 to-black/60"></div>
                    
                    {/* Face Guide Oval */}
                    <div className="relative">
                      <div className="w-52 h-64 border-4 border-white rounded-full shadow-lg bg-transparent animate-pulse">
                        <div className="absolute inset-0 border-2 border-[#00B1B1] rounded-full animate-ping opacity-75"></div>
                      </div>
                      
                      {/* Guide Text */}
                      <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 text-center">
                        <p className="text-white text-sm font-medium bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full">
                          Alinhe seu rosto no centro
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Corner Frame Elements */}
                  <div className="absolute top-4 left-4 w-6 h-6 border-l-3 border-t-3 border-white rounded-tl-lg"></div>
                  <div className="absolute top-4 right-4 w-6 h-6 border-r-3 border-t-3 border-white rounded-tr-lg"></div>
                  <div className="absolute bottom-4 left-4 w-6 h-6 border-l-3 border-b-3 border-white rounded-bl-lg"></div>
                  <div className="absolute bottom-4 right-4 w-6 h-6 border-r-3 border-b-3 border-white rounded-br-lg"></div>
                </div>

                {/* Capture Button */}
                <div className="mt-6 mx-auto max-w-sm">
                  <button 
                    onClick={handleCapture} 
                    className="w-full bg-gradient-to-r from-[#00B1B1] to-[#00A0A0] hover:from-[#00A0A0] hover:to-[#009999] text-white border-none rounded-2xl py-4 px-6 font-semibold text-base transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.95] shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                  >
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                    Capturar Foto
                  </button>
                </div>

                {/* Important Notice */}
                <div className="mt-6 bg-amber-50 rounded-2xl p-4 mx-auto max-w-sm shadow-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-amber-800 font-semibold text-sm mb-2">⚠️ Importante - Remova antes de capturar:</h3>
                      <ul className="text-amber-700 text-xs space-y-1 font-medium">
                        <li>• Óculos (de grau ou sol)</li>
                        <li>• Máscaras faciais</li>
                        <li>• Chapéus ou bonés</li>
                        <li>• Brincos grandes</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Additional Tips */}
                <div className="mt-4 bg-white/95 backdrop-blur-sm rounded-2xl p-4 mx-auto max-w-sm shadow-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#00B1B1] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-gray-800 font-medium text-sm mb-1">Dicas adicionais:</h3>
                      <ul className="text-gray-600 text-xs space-y-1">
                        <li>• Mantenha boa iluminação no rosto</li>
                        <li>• Olhe diretamente para a câmera</li>
                        <li>• Mantenha o rosto centralizado</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="relative w-full max-w-xs mx-auto">
                  <img 
                    src={photo} 
                    alt="Captura realizada" 
                    className="w-full h-auto rounded-3xl shadow-2xl border-4 border-[#00B1B1]" 
                  />
                  <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-2 shadow-lg">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="mt-6 bg-green-50 rounded-2xl p-4 mx-auto max-w-sm">
                  <p className="text-green-800 font-medium text-sm">✅ Captura realizada com sucesso!</p>
                  <p className="text-green-600 text-xs mt-1">Aguarde enquanto processamos sua análise...</p>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="px-4 pb-4">
              <div className="bg-red-50 rounded-2xl p-4 mx-auto max-w-sm border border-red-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-red-800 font-medium text-sm">{errorMessage}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
}
